import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../routes/routeConfig';
import useCardFlipScroll from './useCardFlipScroll';
import BackToTop from './BackToTop';
import './home.css';

const assetPath = (path) => `${process.env.PUBLIC_URL || ''}${path}`;

const topLinks = [
  { label: '财富学院', path: ROUTES.academy },
  { label: '市场研究院', path: ROUTES.market },
];

const solutionLinks = [
  '建立交易认知体系',
  '追踪加密市场雷达',
  '查看美股与黄金信号',
  '匹配 K 线形态',
  '监测大额异动与砸盘风险',
  '寻找支撑阻力位',
];

const serviceGroups = [
  {
    label: '学习',
    links: [
      { label: '财富学院', path: ROUTES.academy },
      { label: '交易书籍', path: ROUTES.academy },
      { label: '认知地图', path: ROUTES.academy },
    ],
  },
  {
    label: '研究',
    links: [
      { label: '加密市场', path: ROUTES.market },
      { label: '黄金趋势', path: ROUTES.marketGold },
      { label: '美股基本面', path: ROUTES.marketStock },
    ],
  },
  {
    label: '工具',
    links: [
      { label: 'K线形态匹配', path: ROUTES.marketToolsPatternMatch },
      { label: '砸盘侦探', path: ROUTES.marketToolsDumpDetective },
      { label: '支撑阻力', path: ROUTES.marketToolsSupportResistance },
    ],
  },
];

const coverageAssets = ['BTC', 'ETH', 'NASDAQ', 'S&P 500', 'GOLD', 'DXY'];

const partners = [
  { name: 'Binance', src: '/logos/binance.svg', className: 'home-brand-binance' },
  { name: 'OKX', src: '/logos/okx.svg', className: 'home-brand-okx' },
  { name: 'Nasdaq', src: '/logos/nasdaq.svg', className: 'home-brand-market' },
  { name: 'Goldman Sachs', src: '/logos/goldman-sachs.svg', className: 'home-brand-goldman' },
  { name: 'PAX Gold', src: '/logos/pax-gold.svg', className: 'home-brand-market' },
];

// 三卡翻转：美股 / 加密 / 黄金，沿用参考项目资产主题
const CARDS = [
  {
    tab: 'stock',
    index: '01',
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 17 9 11 13 15 21 7"/><polyline points="16 7 21 7 21 12"/></svg>',
    title: '美股',
    en: 'Research Hub',
    tagline: '基本面 × 机构持仓 × 个股研报',
    points: ['SEC 基本面档案（财报·估值）', '13F 机构持仓追踪', '个股深度研报'],
  },
  {
    tab: 'crypto',
    index: '02',
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="6" cy="7" r="2"/><circle cx="18" cy="7" r="2"/><circle cx="12" cy="18" r="2"/><line x1="7.6" y1="8.2" x2="10.7" y2="16"/><line x1="16.4" y1="8.2" x2="13.3" y2="16"/><line x1="8" y1="7" x2="16" y2="7"/></svg>',
    title: '加密',
    en: 'Monitor Kit',
    tagline: '异动预警 × 形态识别 × 关键位',
    points: ['砸盘侦探（大额异动·砸盘风险）', 'K线形态自动匹配', '支撑阻力位计算'],
  },
  {
    tab: 'gold',
    index: '03',
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M2 16l2.2-4h15.6L22 16z"/><path d="M6.5 12l1.6-3h7.8l1.6 3"/></svg>',
    title: '黄金',
    en: 'Gold Dossier',
    tagline: '趋势雷达 × 内外盘溢价 × 历史',
    points: ['维加斯通道趋势雷达', '内外盘折算 / 溢价监控', '多周期历史 K 线'],
  },
];

export default function HomePage() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const stickyRef = useRef(null);
  const containerRef = useRef(null);
  const headerRef = useRef(null);
  const bridgeRef = useRef(null);
  useCardFlipScroll({ stickyRef, containerRef, headerRef });

  // 过渡区浮现：进入视口时淡入上移
  useEffect(() => {
    const el = bridgeRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.classList.add('is-visible');
            io.unobserve(el);
          }
        });
      },
      { threshold: 0.3 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const go = (path) => {
    setMenuOpen(false);
    navigate(path);
  };

  const goMarketTab = useCallback((tab) => {
    const TAB_PATHS = { crypto: ROUTES.market, gold: ROUTES.marketGold, stock: ROUTES.marketStock };
    navigate(TAB_PATHS[tab] || ROUTES.market);
  }, [navigate]);

  return (
    <main className={`home-polaris${menuOpen ? ' menu-open' : ''}`} aria-label="GHOST 游领社区首页">
      <section className="home-hero-screen">
        <img className="home-hero-bg" data-testid="home-hero-bg" src={assetPath('/images/brand/polaris-bitcoin-desktop.png')} alt="" />
        <div className="home-hero-shade" />

        <header className="home-topbar">
          <button className="home-brand" type="button" onClick={() => go(ROUTES.home)} aria-label="返回首页">
            GHOST
          </button>
          <nav className="home-nav-links" aria-label="主导航">
            {topLinks.map((link) => (
              <button key={`${link.label}-${link.path}`} type="button" onClick={() => go(link.path)}>
                {link.label}
              </button>
            ))}
          </nav>
          <button
            className="home-menu-trigger"
            type="button"
            aria-label="打开导航菜单"
            aria-expanded={menuOpen}
            aria-controls="homeSiteMenu"
            onClick={() => setMenuOpen(true)}
          >
            <span>导航</span>
            <i aria-hidden="true" />
          </button>
        </header>

        <section className="home-hero-copy">
          <h1>
            <span className="home-swap-wrap">
              <span className="home-swap-front">GHOST</span>
              <span className="home-swap-back" aria-hidden="true">财富始于认知，机会来自研究</span>
            </span>
          </h1>
          <p className="home-intro">
            游领社区为加密交易者提供从入门到进阶的完整路径——财富认知文章、交易书籍、K 线工具、链上分析、机构持仓追踪，帮你在市场噪音中找到真正有价值的信号。
          </p>
          <div className="home-hero-actions">
            <button className="home-primary-btn" type="button" onClick={() => go(ROUTES.academy)}>
              进入财富学院
            </button>
            <button className="home-ghost-btn" type="button" onClick={() => go(ROUTES.market)}>
              探索市场研究院 →
            </button>
            <button className="home-ghost-btn" type="button" onClick={() => go(ROUTES.community)}>
              加入社群 →
            </button>
          </div>
        </section>

        <footer className="home-partner-strip" aria-label="数据覆盖范围">
          <p>覆盖主流交易所、美股市场与黄金资产的关键市场信号</p>
          <div className="home-partner-logos">
            {partners.map((partner) => (
              <button className={`home-brand-cell ${partner.className}`} type="button" key={partner.name} onClick={() => go(ROUTES.market)}>
                <img className="home-brand-mark" src={assetPath(partner.src)} alt="" />
                <span className="home-brand-name">{partner.name}</span>
              </button>
            ))}
          </div>
        </footer>
      </section>

      {/* ── 过渡区：hero → 翻转卡之间的浮现文案 ── */}
      <section className="home-bridge" ref={bridgeRef}>
        <span className="home-bridge-kicker">RESEARCH TERMINAL · 研究终端</span>
        <h2 className="home-bridge-title">
          把市场噪音
          <em>折叠成可行动的信号</em>
        </h2>
        <p className="home-bridge-sub">Three lines of research · 三条主线，一个终端</p>
      </section>

      <aside
        className="home-site-menu"
        id="homeSiteMenu"
        role="dialog"
        aria-modal="true"
        aria-hidden={!menuOpen}
        aria-label="GHOST 全站导航"
      >
        <button className="home-menu-close" type="button" aria-label="关闭导航菜单" onClick={() => setMenuOpen(false)}>
          ×
        </button>
        <div className="home-menu-grid" role="navigation" aria-label="GHOST 全站导航内容">
          <section className="home-menu-panel home-menu-intro">
            <p className="home-menu-kicker">GHOST 是什么？</p>
            <h2>把市场噪音折叠成可行动的研究路径。</h2>
            <p>
              从财富认知、交易书籍、K 线工具到链上资金和机构持仓，GHOST 帮交易者用更清晰的结构理解机会。
            </p>
          </section>

          <section className="home-menu-panel home-menu-solutions">
            <p className="home-menu-kicker">你想解决什么？</p>
            {solutionLinks.map((item) => (
              <button key={item} type="button" onClick={() => go(ROUTES.market)}>
                {item}
              </button>
            ))}
          </section>

          <button className="home-menu-visual home-menu-visual-academy" type="button" onClick={() => go(ROUTES.academy)}>
            <span>财富学院</span>
            <strong>文章、书单、方法论</strong>
          </button>
          <button className="home-menu-visual home-menu-visual-market" type="button" onClick={() => go(ROUTES.market)}>
            <span>市场研究院</span>
            <strong>Crypto · Gold · US Stocks</strong>
          </button>

          <button className="home-menu-visual home-menu-visual-tools" type="button" onClick={() => go(ROUTES.market)}>
            <span>Pro 工具箱</span>
            <strong>把信号变成交易前检查清单</strong>
          </button>
          <button className="home-menu-visual home-menu-visual-community" type="button" onClick={() => go(ROUTES.community)}>
            <span>游领社区</span>
            <strong>和研究型交易者一起复盘</strong>
          </button>

          <section className="home-menu-contact">
            <p className="home-menu-kicker">开始使用</p>
            <h3>进入 GHOST 的研究终端</h3>
            <div className="home-menu-contact-actions">
              <button type="button" onClick={() => go(ROUTES.community)}>加入社区</button>
              <button type="button" onClick={() => go(ROUTES.login)}>登录账户</button>
            </div>
          </section>
        </div>
      </aside>

      {/* ── 三卡翻转滚动（替换原 bottom-bar + 上拉遮盖 overlay）── */}
      <section className="home-card-sticky" ref={stickyRef}>
        <div className="home-card-sticky-header">
          <h1 ref={headerRef}>三支柱，一条研究主线</h1>
        </div>
        <div className="home-card-container" ref={containerRef}>
          {CARDS.map((card, i) => (
            <article
              className="home-flip-card"
              id={`home-card-${i + 1}`}
              key={card.tab}
              onClick={() => goMarketTab(card.tab)}
            >
              <div className="home-card-front">
                <img src={assetPath(`/images/brand/flip-slice-${i + 1}.png`)} alt="" />
              </div>
              <div className="home-card-back">
                <span className="home-card-index">( {card.index} )</span>
                <div className="home-card-body">
                  <span className="home-card-icon" dangerouslySetInnerHTML={{ __html: card.icon }} />
                  <h2>{card.title}</h2>
                  <span className="home-card-en">{card.en}</span>
                  <p>{card.tagline}</p>
                </div>
                <ul className="home-card-points">
                  {card.points.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>
      </section>

      <BackToTop />
    </main>
  );
}
